/**
 * PermissionGate Component
 * Conditionally renders children based on user permissions
 *
 * Usage Examples:
 *
 * // Single permission
 * <PermissionGate permission="deposits.delete">
 *   <DeleteButton />
 * </PermissionGate>
 *
 * // Any of multiple permissions
 * <PermissionGate anyOf={['deposits.edit', 'deposits.create']}>
 *   <EditForm />
 * </PermissionGate>
 *
 * // All of multiple permissions
 * <PermissionGate allOf={['audit.view_system', 'audit.export']}>
 *   <ExportAuditButton />
 * </PermissionGate>
 *
 * // With fallback content
 * <PermissionGate permission="users.edit_permissions" fallback={<DisabledBadge />}>
 *   <AdminBadge />
 * </PermissionGate>
 *
 * // Invert (show if NOT permitted)
 * <PermissionGate permission="deposits.delete" invert>
 *   <NoDeleteWarning />
 * </PermissionGate>
 */

import React, { ReactNode } from 'react';
import { usePermissions } from '@/hooks/usePermissions';

interface PermissionGateProps {
  /** Single permission ID to check */
  permission?: string;

  /** Array of permission IDs - user needs at least ONE */
  anyOf?: string[];

  /** Array of permission IDs - user needs ALL */
  allOf?: string[];

  /** Children to render if permission check passes */
  children: ReactNode;

  /** Optional fallback to render if permission check fails */
  fallback?: ReactNode;

  /** Invert the check - render children if user DOESN'T have permission */
  invert?: boolean;

  /** Show loading indicator while permissions are loading */
  showLoading?: boolean;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  anyOf,
  allOf,
  children,
  fallback = null,
  invert = false,
  showLoading = false,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // While loading, optionally show nothing or loading indicator
  if (isLoading) {
    if (showLoading) {
      return (
        <span className="inline-block animate-pulse bg-gray-200 rounded h-4 w-16"></span>
      );
    }
    return null;
  }

  // Determine if permission check passes
  let permitted = false;

  if (permission) {
    permitted = hasPermission(permission);
  } else if (anyOf && anyOf.length > 0) {
    permitted = hasAnyPermission(anyOf);
  } else if (allOf && allOf.length > 0) {
    permitted = hasAllPermissions(allOf);
  }

  // Apply inversion if requested
  const shouldRender = invert ? !permitted : permitted;

  return <>{shouldRender ? children : fallback}</>;
};

/**
 * Hook variant for use in non-render contexts
 * Returns boolean for conditional logic outside of JSX
 *
 * Usage:
 *   const canDelete = useHasPermission('deposits.delete');
 *   if (canDelete) { ... }
 */
export const useHasPermission = (permissionId: string): boolean => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) return false;
  return hasPermission(permissionId);
};

/**
 * Hook variant for checking any of multiple permissions
 */
export const useHasAnyPermission = (permissionIds: string[]): boolean => {
  const { hasAnyPermission, isLoading } = usePermissions();
  if (isLoading) return false;
  return hasAnyPermission(permissionIds);
};

/**
 * Hook variant for checking all of multiple permissions
 */
export const useHasAllPermissions = (permissionIds: string[]): boolean => {
  const { hasAllPermissions, isLoading } = usePermissions();
  if (isLoading) return false;
  return hasAllPermissions(permissionIds);
};

export default PermissionGate;
